const API_BASE = '/api/v1/chart';

function authHeaders() {
  const headers = { 'Content-Type': 'application/json' };
  const token = localStorage.getItem('token');
  if (token) headers.Authorization = `Bearer ${token}`;
  return headers;
}

async function errorMessage(response) {
  try {
    const body = await response.json();
    if (typeof body.detail === 'string' && body.detail) return body.detail;
  } catch {
    // The body was not JSON.
  }
  return 'Something went wrong. Please try again.';
}

export async function searchPlaces(query) {
  const response = await fetch(`${API_BASE}/places?q=${encodeURIComponent(query)}`);
  if (!response.ok) throw new Error(await errorMessage(response));
  const data = await response.json();
  return data.places || [];
}

async function streamEvents(url, body, { onChart, onCards, onTransits, onToken, onDone, onError, signal }) {
  try {
    const response = await fetch(url, {
      method: 'POST',
      headers: authHeaders(),
      body: JSON.stringify(body),
      signal,
    });
    if (!response.ok) throw new Error(await errorMessage(response));

    const reader = response.body.getReader();
    const decoder = new TextDecoder();
    let buffer = '';
    let currentEvent = '';
    let doneFired = false;

    while (true) {
      const { done, value } = await reader.read();
      if (done) break;
      buffer += decoder.decode(value, { stream: true });
      const lines = buffer.split('\n');
      buffer = lines.pop() || '';

      for (const line of lines) {
        if (line.startsWith('event: ')) {
          currentEvent = line.slice(7).trim();
        } else if (line.startsWith('data: ')) {
          try {
            const data = JSON.parse(line.slice(6));
            if (currentEvent === 'chart') onChart?.(data);
            else if (currentEvent === 'transits') onTransits?.(data);
            else if (currentEvent === 'cards') onCards?.(data.cards || []);
            else if (currentEvent === 'token') onToken(data.token || '');
            else if (currentEvent === 'error') onError(data.message || 'The reading stopped early.');
            else if (currentEvent === 'done' && !doneFired) {
              doneFired = true;
              onDone(data);
            }
          } catch {
            // skip a partial line
          }
        }
      }
    }

    if (!doneFired) onDone(null);
  } catch (err) {
    if (err.name === 'AbortError') return;
    onError(err.message || 'Something went wrong');
  }
}

export function drawChart(payload, handlers) {
  return streamEvents(`${API_BASE}/natal`, { ...payload, reading: true }, handlers);
}

export function drawDaily(payload, handlers) {
  return streamEvents(`${API_BASE}/daily`, payload, handlers);
}

export function drawDailyView(session, view, handlers) {
  return streamEvents(`${API_BASE}/daily/reading`, { session, view }, handlers);
}

export async function previewDaily(payload) {
  const response = await fetch(`${API_BASE}/daily`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ ...payload, reading: false, view: 'today' }),
  });
  if (!response.ok) throw new Error(await errorMessage(response));
  return response.json();
}

export async function dailyPreview(payload) {
  const response = await fetch(`${API_BASE}/daily/preview`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(payload),
  });
  if (!response.ok) throw new Error(await errorMessage(response));
  return response.json();
}

export function drawSynastry(payload, handlers) {
  return streamEvents(`${API_BASE}/synastry`, payload, handlers);
}

export async function sessionPreview(token) {
  const response = await fetch(`${API_BASE}/session/${encodeURIComponent(token)}`);
  if (!response.ok) throw new Error(await errorMessage(response));
  return response.json();
}

export function askChart(session, question, handlers) {
  return streamEvents(`${API_BASE}/ask`, { session, question }, handlers);
}

export function drawChartTarot(session, handlers) {
  return streamEvents(`${API_BASE}/tarot`, { session }, handlers);
}

export function noteAstrology(tool, result, born) {
  if (typeof navigator !== 'undefined' && navigator.userAgent === 'ReactSnap') return;
  fetch(`${API_BASE}/note`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ tool, result: String(result).slice(0, 80), born }),
    keepalive: true,
  }).catch(() => {});
}

export function noteBannerEvent(variant, event, language) {
  if (typeof navigator !== 'undefined' && navigator.userAgent === 'ReactSnap') return;
  fetch(`${API_BASE}/banner/copy-event`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ variant, event, language }),
    keepalive: true,
  }).catch(() => {});
}

export function noteCopyEvent(variant, event, language) {
  if (typeof navigator !== 'undefined' && navigator.userAgent === 'ReactSnap') return;
  fetch(`${API_BASE}/daily/copy-event`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ variant, event, language }),
    keepalive: true,
  }).catch(() => {});
}

export async function venusSign(payload) {
  const response = await fetch(`${API_BASE}/venus`, {
    method: 'POST',
    headers: authHeaders(),
    body: JSON.stringify(payload),
  });
  if (!response.ok) throw new Error(await errorMessage(response));
  return response.json();
}

const SHARES_KEY = 'nowtarot_shared_charts';

function savedShares() {
  try {
    return JSON.parse(localStorage.getItem(SHARES_KEY) || '{}') || {};
  } catch {
    return {};
  }
}

function rememberShare(id, deleteToken) {
  try {
    const shares = savedShares();
    shares[id] = deleteToken;
    localStorage.setItem(SHARES_KEY, JSON.stringify(shares));
  } catch {
    // private mode
  }
}

export function ownsShare(id) {
  return Boolean(savedShares()[id]);
}

export async function shareChart(session, { includeQa = false } = {}) {
  const response = await fetch(`${API_BASE}/share`, {
    method: 'POST',
    headers: authHeaders(),
    body: JSON.stringify({ session, include_qa: includeQa }),
  });
  if (!response.ok) throw new Error(await errorMessage(response));
  const data = await response.json();
  rememberShare(data.id, data.delete_token);
  return data;
}

export async function getSharedChart(id) {
  const response = await fetch(`${API_BASE}/shared/${encodeURIComponent(id)}`);
  if (!response.ok) {
    const error = new Error(await errorMessage(response));
    error.status = response.status;
    throw error;
  }
  return response.json();
}

export async function stopSharing(id) {
  const token = savedShares()[id];
  const response = await fetch(`${API_BASE}/shared/${encodeURIComponent(id)}`, {
    method: 'DELETE',
    headers: { 'X-Delete-Token': token || '' },
  });
  if (!response.ok && response.status !== 204) throw new Error(await errorMessage(response));
  try {
    const shares = savedShares();
    delete shares[id];
    localStorage.setItem(SHARES_KEY, JSON.stringify(shares));
  } catch {
    // private mode
  }
}

export async function redrawHouses(payload) {
  const response = await fetch(`${API_BASE}/natal`, {
    method: 'POST',
    headers: authHeaders(),
    body: JSON.stringify({ ...payload, reading: false }),
  });
  if (!response.ok) throw new Error(await errorMessage(response));
  return response.json();
}
